import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BBALoader from './BBALoader';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { currentUser, loading: authLoading, authError } = useAuth();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userDataError, setUserDataError] = useState(null);

  useEffect(() => {
    let timeoutId;
    let isMounted = true;

    const fetchUserRole = async () => {
      if (currentUser) {
        try {
          // Safety net only: Firestore reports a real outage itself (~10s "client is offline").
          // On phones the first connection (cellular, long-polling fallback) can take well over
          // 8s, and a short timer bounced signed-in users back to login with valid credentials.
          timeoutId = setTimeout(() => {
            if (isMounted) {
              console.warn('User data fetch timeout - redirecting to login');
              setUserDataError(new Error('User data fetch timeout'));
              setLoading(false);
            }
          }, 30000);

          // Check internal_users collection first (CRM users)
          const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
          if (internalUserDoc.exists()) {
            setUserRole(internalUserDoc.data().role);
          } else {
            // Check users collection (Portal users)
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setUserRole(userData.role);
              
              // If this is a portal user with a linked account, check the account type
              if (userData.accountId) {
                try {
                  const accountDoc = await getDoc(doc(db, 'accounts', userData.accountId));
                  if (accountDoc.exists()) {
                    const accountData = accountDoc.data();
                    setAccountType(accountData.type);
                  }
                } catch (error) {
                  console.error('Error fetching linked account:', error);
                }
              }
            }
          }
          
          if (isMounted) {
            setUserDataError(null);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          if (isMounted) {
            setUserDataError(error);
            setLoading(false);
          }
        } finally {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        }
      } else if (!authLoading) {
        // If auth is done loading and no user, stop loading
        setLoading(false);
      }
    };

    // Only fetch user role if auth is not loading and we have a current user
    if (!authLoading && currentUser) {
      fetchUserRole();
    } else if (!authLoading && !currentUser) {
      // If auth is done loading and no user, stop loading
      setLoading(false);
    }

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [currentUser, authLoading]);

  // If there's an auth error, redirect to login
  if (authError) {
    console.error('Auth error in ProtectedRoute:', authError);
    return <Navigate to="/login" state={{ from: location, error: 'Authentication failed' }} replace />;
  }

  // Show loading while auth is loading or fetching user role
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BBALoader size={128} />
      </div>
    );
  }

  // If there's an error fetching user data, redirect to login
  if (userDataError) {
    console.error('User data error in ProtectedRoute:', userDataError);
    return <Navigate to="/login" state={{ from: location, error: 'Failed to load user data' }} replace />;
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access if required
  if (requiredRole && userRole !== requiredRole) {
    // Redirect based on user role and account type
    if (userRole === 'Owner' || userRole === 'Executive' || userRole === 'Member' || userRole === 'Contractor' || userRole === 'Accountant' || userRole === 'team') {
      return <Navigate to="/crm" replace />;
    } else if (userRole === 'client') {
      // Portal user - check account type
      if (accountType === 'Clients' || accountType === 'clients') {
        return <Navigate to="/portal" replace />;
      } else {
        // Lead account - redirect to start
        return <Navigate to="/start" replace />;
      }
    } else {
      // Default for other roles
      return <Navigate to="/start" replace />;
    }
  }

  return children;
};

export default ProtectedRoute; 